import { memo, useEffect, useState } from 'react'
import { Copy, Check, RefreshCw, ThumbsUp, ThumbsDown, Pencil, Share2 } from 'lucide-react'
import type { Message } from '@/types'
import { ImageViewerModal } from '@/components/image/ImageViewerModal'
import type { AspectRatioValue } from '@/components/image/ImageViewerModal'
import { SaveSkillBox } from './SaveSkillBox'
import { QuestionCard } from './QuestionCard'
import MessageCompose from '@/components/tools/MessageCompose'
import RecipeDisplay from '@/components/tools/RecipeDisplay'
import VisualizeWidget from '@/components/tools/VisualizeWidget'
import WeatherCard from '@/components/tools/WeatherCard'
import SportsCard from '@/components/tools/SportsCard'
import PresentFiles from '@/components/tools/PresentFiles'
import { ToolErrorBoundary } from '@/components/tools/ToolErrorBoundary'
import { TriggerSuggestionCard } from '@/components/tools/TriggerSuggestionCard'
import { InterfaceSuggestionCard } from '@/components/tools/InterfaceSuggestionCard'
import { AutomationProfileCard } from '@/components/tools/AutomationProfileCard'
import { CredentialRequestCard } from '@/components/tools/CredentialRequestCard'
import { TakeoverRequestCard } from '@/components/tools/TakeoverRequestCard'
import { SuggestAgentModeCard } from '@/components/tools/SuggestAgentModeCard'
import { SmoothStreamingText } from './SmoothStreamingText'
import { ImageGeneration } from '@/components/ui/ImageGeneration'
import { SineThinkingLoader } from '@/components/ui/SineThinkingLoader'

interface ChatMessageProps {
  message: Message
  onRegenerate?: () => void
  isLast?: boolean
  conversationHasSkillCreator?: boolean
  onAskUserSubmit?: (answers: Record<number, string[]>) => void
  onCredentialSubmit?: (credentials: { username: string; password: string }) => void
  onTakeoverAction?: (action: 'internal' | 'user_browser' | 'continue') => void
  // Passed from parent to avoid useApp() inside memo (prevents blinking re-renders)
  theme?: 'dark' | 'light'
  modelLabel?: string
  language?: 'no' | 'en'
  /** Called when user submits a text edit from the image viewer */
  onImageEditSubmit?: (prompt: string, imageUrl: string) => void
  /** Called when user submits an inpaint from the image viewer */
  onImageInpaintSubmit?: (prompt: string, imageUrl: string, maskDataUrl: string) => void
  /** Called when user selects an aspect ratio from the image viewer */
  onImageAspectRatioChange?: (ratio: AspectRatioValue, imageUrl: string) => void
  /** Called when user accepts a deeper tool/workflow handoff card */
  onSwitchToAgent?: () => void
}

const BUILD_PROGRESS_MESSAGES = [
  'Bygger',
  'Setter sammen informasjon',
  'Former grensesnittet',
  'Gjør klar preview',
]

function BuildProgressIndicator() {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    const interval = window.setInterval(() => {
      setIndex(prev => (prev + 1) % BUILD_PROGRESS_MESSAGES.length)
    }, 1800)
    return () => window.clearInterval(interval)
  }, [])

  return (
    <div className="thinking-indicator thinking-indicator--build">
      <span className="thinking-text">{BUILD_PROGRESS_MESSAGES[index]}</span>
    </div>
  )
}

export const ChatMessage = memo(function ChatMessage({ message, onRegenerate, isLast, conversationHasSkillCreator, onAskUserSubmit, onCredentialSubmit, onTakeoverAction, theme: themeProp, modelLabel: _modelLabelProp, language, onImageEditSubmit, onImageInpaintSubmit, onImageAspectRatioChange, onSwitchToAgent }: ChatMessageProps) {
  const [copied, setCopied] = useState(false)
  const [liked, setLiked] = useState<boolean | null>(null)
  const [showSaveSkill, setShowSaveSkill] = useState(true)
  const [imageViewerOpen, setImageViewerOpen] = useState(false)
  const [isImageEditing, setIsImageEditing] = useState(false)
  const [editedImageUrl] = useState<string | null>(null)
  // Use props when available to avoid subscribing to the full app context (prevents blinking)
  const effectiveTheme = themeProp ?? 'dark'
  const chatLogoSrc = effectiveTheme === 'light' ? '/Sine-sort.svg' : '/Sinev6.svg'
  const messageModelBadge = message.modelProfile === 'max' ? 'Max' : message.modelProfile === 'pro' ? 'Pro' : null
  const handleCopy = async () => {
    await navigator.clipboard.writeText(message.content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const isUser = message.role === 'user'
  const showInlineActions = Boolean(!message.isStreaming && message.content && !message.askUserInput && !message.toolData)
  const showBuildProgress = Boolean(
    isLast
    && !message.isStreaming
    && !message.askUserInput
    && !message.toolData
    && /bygger dette n(?:å|aa)|bygger n(?:å|aa)|starter bygging|setter opp dette n(?:å|aa)/i.test(message.content.trim()),
  )

  if (isUser) {
    return (
      <div className="message-user animate-fade-in">
        <div className="message-user-bubble">
          <div>{message.content}</div>
          {message.attachments && message.attachments.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
              {message.attachments.map((attachment, index) => (
                <div
                  key={`${attachment.name}-${index}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 10px',
                    borderRadius: 999,
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    fontSize: 12,
                    color: '#D4D4D8',
                  }}
                >
                  <span>{attachment.name}</span>
                  <span style={{ color: '#8D8D91' }}>
                    {attachment.status === 'uploaded' ? 'lagret' : attachment.status === 'inline_only' ? 'vedlagt' : 'lokal'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={`message-assistant animate-fade-in${showInlineActions ? ' has-inline-actions' : ''}`}>
      {/* Avatar — full logo (ikon + tekst) + model badge i samme rad */}
      <div className="message-avatar-row">
        <img
          src={chatLogoSrc}
          alt="Sine"
          className="message-logo-img"
        />
        {messageModelBadge && <span className="message-badge-clean">{messageModelBadge}</span>}
      </div>

      {/* Content */}
      <div className="message-body">

        {/* Interactive question card (ask_user_input_v0) */}
        {message.askUserInput && !message.askUserInput.answered && (
          <QuestionCard
            questions={message.askUserInput.questions}
            answered={message.askUserInput.answered}
            onSubmit={(answers) => onAskUserSubmit?.(answers)}
          />
        )}

        {/* Tool renderers — wrapped in ErrorBoundary to prevent chat crashes */}
        {message.toolData && (
          <div className="tool-renderer-wrapper">
            {message.toolData.type === 'message_compose' && (
              <ToolErrorBoundary toolName="MessageCompose">
                <MessageCompose data={message.toolData.data} />
              </ToolErrorBoundary>
            )}
            {message.toolData.type === 'recipe_display' && (
              <ToolErrorBoundary toolName="RecipeDisplay">
                <RecipeDisplay data={message.toolData.data} />
              </ToolErrorBoundary>
            )}
            {message.toolData.type === 'visualize_widget' && (
              <ToolErrorBoundary toolName="VisualizeWidget">
                <VisualizeWidget data={message.toolData.data} />
              </ToolErrorBoundary>
            )}
            {message.toolData.type === 'weather_card' && (
              <ToolErrorBoundary toolName="WeatherCard">
                <WeatherCard data={message.toolData.data} />
              </ToolErrorBoundary>
            )}
            {message.toolData.type === 'sports_card' && (
              <ToolErrorBoundary toolName="SportsCard">
                <SportsCard data={message.toolData.data} />
              </ToolErrorBoundary>
            )}
            {message.toolData.type === 'present_files' && (
              <ToolErrorBoundary toolName="PresentFiles">
                <PresentFiles data={message.toolData.data} />
              </ToolErrorBoundary>
            )}
            {message.toolData.type === 'trigger_suggestion' && (
              <ToolErrorBoundary toolName="TriggerSuggestionCard">
                <TriggerSuggestionCard
                  data={message.toolData.data}
                  onConfirm={(triggerData) => {
                    window.dispatchEvent(new CustomEvent('sine:trigger-created', { detail: triggerData }))
                    console.log('[TriggerSuggestion] confirmed:', triggerData)
                  }}
                />
              </ToolErrorBoundary>
            )}
            {message.toolData.type === 'interface_suggestion' && (
              <ToolErrorBoundary toolName="InterfaceSuggestionCard">
                <InterfaceSuggestionCard data={message.toolData.data} />
              </ToolErrorBoundary>
            )}
            {message.toolData.type === 'automation_profile' && (
              <ToolErrorBoundary toolName="AutomationProfileCard">
                <AutomationProfileCard data={message.toolData.data} />
              </ToolErrorBoundary>
            )}
            {message.toolData.type === 'credential_request' && (
              <ToolErrorBoundary toolName="CredentialRequestCard">
                <CredentialRequestCard data={message.toolData.data} onSubmit={onCredentialSubmit} />
              </ToolErrorBoundary>
            )}
            {message.toolData.type === 'takeover_request' && (
              <ToolErrorBoundary toolName="TakeoverRequestCard">
                <TakeoverRequestCard
                  data={message.toolData.data}
                  isActive={Boolean(message.toolData.data?.isActive)}
                  onUseInternal={() => onTakeoverAction?.('internal')}
                  onUseUserBrowser={() => onTakeoverAction?.('user_browser')}
                  onContinue={() => onTakeoverAction?.('continue')}
                />
              </ToolErrorBoundary>
            )}
            {message.toolData.type === 'suggest_agent_mode' && (
              <ToolErrorBoundary toolName="SuggestAgentModeCard">
                <SuggestAgentModeCard
                  data={message.toolData.data}
                  onSwitchToAgent={onSwitchToAgent}
                  onContinueInChat={() => {}}
                />
              </ToolErrorBoundary>
            )}
          </div>
        )}

        {/* Image generation with animated reveal */}
        {message.imageGeneration && (
          <div className="my-2">
            <ImageGeneration
              completed={message.imageGeneration.state === 'completed' || message.imageGeneration.state === 'failed'}
              language={language === 'en' ? 'en' : 'no'}
            >
              {message.imageGeneration.state === 'completed' && (editedImageUrl || message.imageGeneration.url) ? (
                // Clickable image with hover overlay
                <div
                  className="img-generation-hover-wrapper"
                  onClick={() => setImageViewerOpen(true)}
                  title={language === 'en' ? 'Click to open' : 'Klikk for å åpne'}
                >
                  <img
                    src={editedImageUrl || message.imageGeneration.url!}
                    alt={message.imageGeneration.prompt || 'Generert bilde'}
                    className="img-generation-fill"
                  />
                  {/* Hover overlay with Rediger + Del buttons */}
                  <div className="img-generation-overlay">
                    <button
                      className="img-generation-overlay-btn img-generation-overlay-btn-left"
                      onClick={(e) => { e.stopPropagation(); setImageViewerOpen(true) }}
                    >
                      <Pencil size={12} />
                      {language === 'en' ? 'Edit' : 'Rediger'}
                    </button>
                    <button
                      className="img-generation-overlay-btn img-generation-overlay-btn-right"
                      onClick={async (e) => {
                        e.stopPropagation()
                        const url = editedImageUrl || message.imageGeneration!.url!
                        if (navigator.share) {
                          try { await navigator.share({ url, title: message.imageGeneration!.prompt || 'Generert bilde' }) } catch { /* cancelled */ }
                        } else {
                          await navigator.clipboard.writeText(url)
                        }
                      }}
                    >
                      <Share2 size={14} />
                    </button>
                  </div>
                </div>
              ) : message.imageGeneration.state === 'failed' ? (
                <div className="flex items-center justify-center p-8 text-sm" style={{ color: '#ef4444', width: '100%', height: '100%' }}>
                  {language === 'en' ? 'Could not generate image. Try again.' : 'Kunne ikke generere bildet. Prøv igjen.'}
                </div>
              ) : (
                // Placeholder while generating — fills the 16:9 container
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(135deg, #1A1A1A 0%, #0D0D0D 100%)',
                  }}
                />
              )}
            </ImageGeneration>

            {/* Image Viewer Modal */}
            {imageViewerOpen && (editedImageUrl || message.imageGeneration.url) && (
              <ImageViewerModal
                imageUrl={editedImageUrl || message.imageGeneration.url!}
                prompt={message.imageGeneration.prompt}
                language={language === 'en' ? 'en' : 'no'}
                onClose={() => setImageViewerOpen(false)}
                isEditing={isImageEditing}
                editedImageUrl={editedImageUrl}
                onEditSubmit={async (prompt, imageUrl) => {
                  setIsImageEditing(true)
                  try {
                    await onImageEditSubmit?.(prompt, imageUrl)
                  } finally {
                    setIsImageEditing(false)
                  }
                }}
                onInpaintSubmit={async (prompt, imageUrl, maskDataUrl) => {
                  setIsImageEditing(true)
                  try {
                    await onImageInpaintSubmit?.(prompt, imageUrl, maskDataUrl)
                  } finally {
                    setIsImageEditing(false)
                  }
                }}
                onAspectRatioChange={(ratio, imageUrl) => {
                  onImageAspectRatioChange?.(ratio, imageUrl)
                }}
              />
            )}
          </div>
        )}

        <div className="prose">
          {message.isStreaming && !message.content ? (
            <div className="sns-thinking-bubble">
              <SineThinkingLoader size="sm" label="Tenker" />
            </div>
          ) : showBuildProgress ? (
            <BuildProgressIndicator />
          ) : (
            <SmoothStreamingText
              content={message.content}
              isStreaming={message.isStreaming}
              animateOnMount={false}
              animationKey={`${message.id}-${message.isStreaming ? 'streaming' : 'done'}`}
            />
          )}
        </div>

        {/* SaveSkillBox — vises etter siste assistent-melding i /skill-creator samtaler */}
        {isLast && conversationHasSkillCreator && !message.isStreaming && message.role === 'assistant' && showSaveSkill && (
          <SaveSkillBox
            suggestedName=""
            suggestedDescription=""
            suggestedSystemPrompt={message.content.slice(0, 500)}
            onDismiss={() => setShowSaveSkill(false)}
          />
        )}

        {/* Action buttons */}
        {showInlineActions && (
          <div className="message-actions">
            <button
              className="message-action-btn"
              onClick={handleCopy}
              title={copied ? 'Kopiert!' : 'Kopier'}
              style={{ color: copied ? '#1A93FE' : undefined }}
            >
              {copied ? <Check size={13} /> : <Copy size={13} />}
            </button>
            <button
              className="message-action-btn"
              onClick={() => setLiked(true)}
              title="Bra svar"
              style={{ color: liked === true ? '#1A93FE' : undefined }}
            >
              <ThumbsUp size={13} />
            </button>
            <button
              className="message-action-btn"
              onClick={() => setLiked(false)}
              title="Dårlig svar"
              style={{ color: liked === false ? '#ef4444' : undefined }}
            >
              <ThumbsDown size={13} />
            </button>
            {isLast && onRegenerate && (
              <button
                className="message-action-btn"
                onClick={onRegenerate}
                title="Generer på nytt"
              >
                <RefreshCw size={13} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}, (prev, next) => {
  // Only re-render if the message content, streaming state, or isLast changed
  // This prevents blinking when other messages in the conversation update
  return (
    prev.message.id === next.message.id &&
    prev.message.content === next.message.content &&
    prev.message.isStreaming === next.message.isStreaming &&
    prev.isLast === next.isLast &&
    prev.message.askUserInput === next.message.askUserInput &&
    prev.message.toolData === next.message.toolData &&
    prev.conversationHasSkillCreator === next.conversationHasSkillCreator &&
    prev.theme === next.theme &&
    prev.modelLabel === next.modelLabel &&
    // Image generation: re-render when state or url changes
    prev.message.imageGeneration?.state === next.message.imageGeneration?.state &&
    prev.message.imageGeneration?.url === next.message.imageGeneration?.url
  )
})
